import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useSearch } from 'wouter';
import Navigation from "@/components/navigation";
import StarfieldBackground from "@/components/starfield-background";
import { Filter, Loader2, AlertCircle, Maximize2, Minimize2 } from "lucide-react";
import { apiService } from "@/lib/api";
// Map game abbreviation (lowercase) → logo path under /images/game-logos/
const GAME_LOGO_MAP = {
    '2xko': '2xko.png',
    '3s': '3s.png',
    'bbcf': 'bbcf.png',
    'cotw': 'cotw.png',
    'cvs1': 'cvs1.png',
    'cvs2': 'cvs2.png',
    'dbfz': 'dbfz-logo.png',
    'gbvsr': 'gbvsr.png',
    'gg+r': 'ggplusR.png',
    'ggplusr': 'ggplusR.png',
    'ggst': 'ggst.png',
    'ki': 'ki.png',
    'kof15': 'kof15.png',
    'mk1': 'mk1.svg',
    'mvc2': 'mvc2.png',
    'rev2': 'rev2.png',
    'sc6': 'sc6.png',
    'sf6': 'sf6.png',
    'sfa2': 'sfa2.png',
    'sfa3': 'sfa3.png',
    'st': 'st.png',
    'tk8': 'tk8.png',
    'umvc3': 'umvc3.png',
    'uni2': 'uni2.png',
    'usf4': 'usf41000.png',
    'vf5': 'vf5revo.png',
    'vf5revo': 'vf5revo.png',
    'vsav': 'vsav.png',
};
const getGameLogo = (abbreviation) => {
    const file = GAME_LOGO_MAP[abbreviation.toLowerCase()];
    return file ? `/images/game-logos/${file}` : null;
};
// Color assigned client-side by game ID (no color column in DB)
const PALETTES = {
    // Bold, high-contrast — distinguish many games at a glance
    master: [
        "#DC2626", "#2563EB", "#7C3AED", "#D97706",
        "#059669", "#0891B2", "#DB2777", "#EA580C",
        "#65A30D", "#991B1B", "#1D4ED8", "#9333EA",
    ],
    // Cool blues, purples, teals — broadcast/media booth feel
    commentators: [
        "#1D4ED8", "#7C3AED", "#0891B2", "#4338CA",
        "#0E7490", "#6D28D9", "#3B82F6", "#5B21B6",
        "#1E40AF", "#0369A1", "#818CF8", "#475569",
    ],
    // Greens and ambers — operations/crew feel
    volunteers: [
        "#15803D", "#B45309", "#059669", "#4D7C0F",
        "#0F766E", "#C2410C", "#166534", "#92400E",
        "#16A34A", "#3F6212", "#065F46", "#78350F",
    ],
};
const GAME_COLORS = {};
const getGameColor = (gameId, scheduleType) => {
    if (gameId === null)
        return "#6B7280";
    const key = `${scheduleType}-${gameId}`;
    if (!GAME_COLORS[key]) {
        const palette = PALETTES[scheduleType];
        const idx = Object.keys(GAME_COLORS).filter(k => k.startsWith(scheduleType)).length % palette.length;
        GAME_COLORS[key] = palette[idx];
    }
    return GAME_COLORS[key];
};
const START_HOUR = 10; // 10 AM
const END_HOUR = 24; // 12 AM 
const ROW_HEIGHT = 80; // px
const formatHour = (h) => {
    const adj = h % 24;
    if (adj === 0)
        return "12:00 AM";
    if (adj < 12)
        return `${adj}:00 AM`;
    if (adj === 12)
        return "12:00 PM";
    return `${adj - 12}:00 PM`;
};
export default function ScheduleDisplay() {
    const search = useSearch();
    const scheduleType = (new URLSearchParams(search).get('type') ?? 'master');
    const [blocks, setBlocks] = useState([]);
    const [poolViews, setPoolViews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [fullscreen, setFullscreen] = useState(false);
    const [selectedGameId, setSelectedGameId] = useState(null);
    const [currentMinute, setCurrentMinute] = useState(() => {
        const now = new Date();
        return now.getHours() * 60 + now.getMinutes();
    });
    useEffect(() => {
        setLoading(true);
        setError(null);
        apiService.getScheduleBlocks()
            .then(data => {
            setBlocks(data);
            if (data.length > 0) {
                const earliest = data.reduce((min, b) => b.startTime < min ? b.startTime : min, data[0].startTime);
                setSelectedDate(earliest.slice(0, 10));
            }
        })
            .catch(() => setError("Failed to load schedule. Please try again."))
            .finally(() => setLoading(false));
        if (scheduleType === 'volunteers') {
            apiService.getAllPoolViews().then(setPoolViews).catch(console.error);
        }
    }, [scheduleType]);
    // Tick every minute so the current-time indicator stays accurate
    useEffect(() => {
        const tick = () => {
            const now = new Date();
            setCurrentMinute(now.getHours() * 60 + now.getMinutes());
        };
        const id = setInterval(tick, 60000);
        return () => clearInterval(id);
    }, []);
    const poolViewsByBlock = poolViews.reduce((acc, v) => {
        if (v.scheduleBlockId != null) {
            acc[v.scheduleBlockId] = [...(acc[v.scheduleBlockId] ?? []), v];
        }
        return acc;
    }, {});
    const availableDates = Array.from(new Set(blocks.map(b => b.startTime.slice(0, 10)))).sort();
    const filteredBlocks = blocks.filter(b => b.startTime.startsWith(selectedDate));
    const games = [];
    const seenIds = new Set();
    for (const b of filteredBlocks) {
        if (b.gameId === null)
            continue;
        if (!seenIds.has(b.gameId)) {
            seenIds.add(b.gameId);
            games.push({
                gameId: b.gameId,
                gameName: b.gameName ?? `Game ${b.gameId}`,
                gameAbbreviation: b.gameAbbreviation ?? `G${b.gameId}`,
                color: getGameColor(b.gameId, scheduleType),
            });
        }
    }
    const getRelativeHour = (timeStr) => {
        const base = new Date(selectedDate + 'T00:00:00');
        const time = new Date(timeStr);
        return Math.round((time.getTime() - base.getTime()) / (1000 * 60 * 60));
    };
    const timeSlots = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR);
    const buildGrid = () => {
        const spanUntil = new Map();
        return timeSlots.map((hour) => games.map((game) => {
            if (hour < (spanUntil.get(game.gameId) ?? 0)) {
                return { kind: 'skip' };
            }
            const block = filteredBlocks.find(b => getRelativeHour(b.startTime) === hour && b.gameId === game.gameId);
            if (block) {
                const rowSpan = getRelativeHour(block.endTime) - hour;
                spanUntil.set(game.gameId, getRelativeHour(block.endTime));
                return { kind: 'block', block, rowSpan };
            }
            return { kind: 'empty' };
        }));
    };
    const grid = buildGrid();
    const SCHEDULE_TYPE_LABELS = {
        master: 'Master Schedule',
        commentators: 'Commentator Schedule',
        volunteers: 'Volunteer Schedule',
    };
    const renderCellContent = (block) => {
        if (scheduleType === 'commentators') {
            return (_jsxs("div", { style: { fontSize: '0.7rem', lineHeight: '1.3', fontFamily: 'sans-serif' }, children: [block.commentator1Name && (_jsx("div", { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: block.commentator1Name, children: block.commentator1Name })), block.commentator2Name && (_jsx("div", { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: block.commentator2Name, children: block.commentator2Name })), !block.commentator1Name && !block.commentator2Name && _jsx("div", { className: "opacity-40", children: "\u2014" })] }));
        }
        if (scheduleType === 'volunteers') {
            const entries = poolViewsByBlock[block.scheduleBlockId] ?? [];
            return (_jsx("div", { className: "text-base font-sans font-normal leading-snug space-y-1", children: entries.length > 0
                    ? entries.map(e => _jsx("div", { children: e.gamertag ?? '—' }, e.scheduleBlockPoolId))
                    : _jsx("div", { className: "opacity-40", children: "\u2014" }) }));
        }
        // master
        return (_jsx("div", { className: "text-base font-sans font-semibold leading-tight", children: block.blockName }));
    };
    const dayLabel = selectedDate
        ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
            weekday: 'long', month: 'long', day: 'numeric'
        }).toUpperCase()
        : "";
    const dateSelector = (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Filter, { className: "w-4 h-4 text-gray-400" }), _jsx("select", { value: selectedDate, onChange: (e) => setSelectedDate(e.target.value), disabled: loading || availableDates.length === 0, className: "px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-txs-red text-sm disabled:opacity-50", children: availableDates.map(d => (_jsx("option", { value: d, className: "bg-space-dark", children: new Date(d + 'T12:00:00').toLocaleDateString('en-US', {
                        weekday: 'long', month: 'long', day: 'numeric'
                    }) }, d))) }), _jsx("button", { onClick: () => setFullscreen(f => !f), title: fullscreen ? "Exit fullscreen" : "Fullscreen view", className: "p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white transition-colors", children: fullscreen ? _jsx(Minimize2, { className: "w-4 h-4" }) : _jsx(Maximize2, { className: "w-4 h-4" }) })] }));
    const scheduleGrid = (_jsxs(_Fragment, { children: [loading && (_jsxs("div", { className: "flex items-center justify-center gap-3 py-24 text-gray-400", children: [_jsx(Loader2, { className: "w-6 h-6 animate-spin" }), _jsx("span", { className: "font-orbitron text-sm", children: "Loading schedule..." })] })), error && (_jsxs("div", { className: "flex items-center gap-3 p-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400", children: [_jsx(AlertCircle, { className: "w-5 h-5 flex-shrink-0" }), _jsx("span", { children: error })] })), !loading && !error && filteredBlocks.length === 0 && (_jsx("div", { className: "text-center py-24 text-gray-500", children: _jsx("p", { className: "font-orbitron text-sm", children: "No schedule blocks found for this date." }) })), !loading && !error && filteredBlocks.length > 0 && (_jsx("div", { className: fullscreen ? "flex-1 overflow-auto" : "overflow-auto rounded-xl border border-white/10", children: _jsxs("table", { className: "border-collapse text-sm", style: { tableLayout: 'fixed', width: '100%' }, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "border border-gray-600 p-3 bg-gray-800 font-orbitron font-bold text-center align-middle sticky left-0 top-0 z-30 text-xs", style: { width: 90, whiteSpace: 'pre-line' }, children: dayLabel }), games.map(game => {
                                        const isSelected = selectedGameId === game.gameId;
                                        const isDimmed = selectedGameId !== null && !isSelected;
                                        return (_jsx("th", { onClick: () => setSelectedGameId(id => id === game.gameId ? null : game.gameId), className: "border border-gray-600 p-2 font-orbitron font-bold text-center sticky top-0 z-20 cursor-pointer transition-all", style: {
                                                backgroundColor: game.color + (isSelected ? 'FF' : isDimmed ? '33' : '99'),
                                                width: 130,
                                                opacity: isDimmed ? 0.5 : 1,
                                                outline: isSelected ? `2px solid ${game.color}` : undefined,
                                            }, title: `Click to highlight ${game.gameName}`, children: (() => {
                                                const logo = getGameLogo(game.gameAbbreviation);
                                                return logo
                                                    ? _jsx("img", { src: logo, alt: `${game.gameAbbreviation} – ${game.gameName}`, title: game.gameName, className: "h-10 w-auto mx-auto object-contain" })
                                                    : (_jsxs(_Fragment, { children: [_jsx("div", { className: "text-sm", children: game.gameAbbreviation }), _jsx("div", { className: "text-xs font-sans font-normal text-gray-200 mt-1 normal-case", children: game.gameName })] }));
                                            })() }, game.gameId));
                                    })] }) }), _jsx("tbody", { children: timeSlots.map((hour, rowIdx) => {
                                const row = grid[rowIdx];
                                const allEmpty = row.every(c => c.kind === 'empty');
                                // Current-time indicator: fractional offset within this hour row
                                const rowStartMinute = hour * 60;
                                const rowEndMinute = rowStartMinute + 60;
                                const showTimeLine = currentMinute >= rowStartMinute && currentMinute < rowEndMinute;
                                const timeLineOffset = ((currentMinute - rowStartMinute) / 60) * (allEmpty ? 40 : ROW_HEIGHT);
                                const rowHeight = allEmpty ? 40 : ROW_HEIGHT;
                                return (_jsxs("tr", { style: { position: 'relative' }, children: [_jsx("td", { className: "border border-gray-700 px-2 text-center text-xs font-semibold text-gray-400 bg-gray-900 sticky left-0 z-10", style: { height: rowHeight, width: 90 }, children: formatHour(hour) }), allEmpty ? (_jsxs("td", { colSpan: games.length, className: "border border-gray-700 text-center font-orbitron font-bold tracking-widest text-gray-500 bg-gray-800/30 text-xs", style: { height: rowHeight, position: 'relative' }, children: ["OPEN FOR CASUALS", showTimeLine && (_jsx("div", { style: {
                                                        position: 'absolute', left: 0, right: 0,
                                                        top: timeLineOffset,
                                                        height: 2, background: '#EF4444',
                                                        boxShadow: '0 0 6px #EF4444',
                                                        zIndex: 5, pointerEvents: 'none',
                                                    } }))] })) : (row.map((cell, gameIdx) => {
                                            if (cell.kind === 'skip')
                                                return null;
                                            const game = games[gameIdx];
                                            const isSelected = selectedGameId === game.gameId;
                                            const isDimmed = selectedGameId !== null && !isSelected;
                                            if (cell.kind === 'empty') {
                                                return (_jsx("td", { className: "border border-gray-700/60", style: {
                                                        height: rowHeight,
                                                        backgroundColor: isDimmed ? 'rgba(17,24,39,0.8)' : game.color + '0D',
                                                        backgroundImage: isDimmed ? 'none' : `radial-gradient(circle, ${game.color}22 1px, transparent 1px)`,
                                                        backgroundSize: '16px 16px',
                                                        opacity: isDimmed ? 0.4 : 1,
                                                        transition: 'opacity 0.2s, background-color 0.2s',
                                                        position: 'relative',
                                                    }, children: showTimeLine && (_jsx("div", { style: {
                                                            position: 'absolute', left: 0, right: 0,
                                                            top: timeLineOffset,
                                                            height: 2, background: '#EF4444',
                                                            boxShadow: '0 0 6px #EF4444',
                                                            zIndex: 5, pointerEvents: 'none',
                                                        } })) }, game.gameId));
                                            }
                                            return (_jsxs("td", { rowSpan: cell.rowSpan, className: "border border-gray-600 p-3 text-center align-middle", style: {
                                                    backgroundColor: (() => {
                                                        if (isDimmed)
                                                            return game.color + '44';
                                                        if (scheduleType === 'commentators') {
                                                            const hasCommentator = cell.block.commentator1Name || cell.block.commentator2Name;
                                                            return game.color + (hasCommentator ? 'CC' : '1A');
                                                        }
                                                        return game.color + 'CC';
                                                    })(),
                                                    color: '#fff',
                                                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                                                    opacity: isDimmed ? 0.35 : 1,
                                                    transition: 'opacity 0.2s, background-color 0.2s',
                                                    position: 'relative',
                                                }, children: [renderCellContent(cell.block), showTimeLine && (_jsx("div", { style: {
                                                            position: 'absolute', left: 0, right: 0,
                                                            top: timeLineOffset,
                                                            height: 2, background: '#EF4444',
                                                            boxShadow: '0 0 6px #EF4444',
                                                            zIndex: 5, pointerEvents: 'none',
                                                        } }))] }, game.gameId));
                                        }))] }, hour));
                            }) })] }) }))] }));
    // ── Fullscreen / kiosk mode ────────────────────────────────────────────────
    if (fullscreen) {
        return (_jsxs("div", { className: "fixed inset-0 z-50 bg-space-dark text-white flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between px-4 py-2 border-b border-white/10 bg-gray-900/80 backdrop-blur-sm flex-shrink-0", children: [_jsx("span", { className: "font-orbitron font-bold text-txs-red text-lg", children: SCHEDULE_TYPE_LABELS[scheduleType] }), dateSelector] }), scheduleGrid] }));
    }
    // ── Normal mode ────────────────────────────────────────────────────────────
    return (
    // No overflow-hidden here — starfield uses position:fixed so it doesn't need it,
    // and removing it lets the grid's overflow-x-auto actually show a scrollbar.
    _jsxs("div", { className: "admin-page min-h-screen bg-space-dark text-white relative", children: [_jsx(StarfieldBackground, {}), _jsx(Navigation, {}), _jsx("main", { className: "pt-20 pb-16 px-4 relative z-10", children: _jsxs("div", { className: "max-w-full mx-auto", children: [_jsxs("div", { className: "mb-6 flex items-center justify-between flex-wrap gap-4", children: [_jsx("h1", { className: "font-orbitron font-bold text-4xl text-txs-red", children: SCHEDULE_TYPE_LABELS[scheduleType] }), dateSelector] }), scheduleGrid] }) })] }));
}
